import { useEffect, useState } from 'react';
import axios from 'axios';
import PayrollLayout from '../../components/layout/PayrollLayout';
import StaffPayrollInfo from '../../components/StaffPayrollInfo';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Users, Search, ChevronRight, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PAYROLL — Employee Details (STRICTLY READ-ONLY).
// Search/select staff and view only the payroll-relevant information.
export default function PayrollEmployeesPage() {
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const month = new Date().toISOString().slice(0, 7);
    axios.get(`${API}/admin/payroll/staff`, { params: { month, show_all: true } })
      .then((r) => setStaff(r.data?.staff || []))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load staff'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = staff.filter((s) =>
    !search.trim() || (s.name || '').toLowerCase().includes(search.trim().toLowerCase())
    || (s.email || '').toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <PayrollLayout>
      <div className="space-y-4" data-testid="payroll-employees-page">
        <div>
          <h1 className="text-xl font-barlow font-bold tracking-wider uppercase flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" strokeWidth={1.5} /> Employee Details
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Read-only — payroll-relevant information only. Nothing can be edited here.
          </p>
        </div>
        <div className="relative max-w-sm">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-8 bg-card border-border rounded-sm"
            data-testid="payroll-employee-search"
          />
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground animate-pulse">Loading staff…</p>
        ) : filtered.length === 0 ? (
          <Card className="bg-card border-border rounded-sm"><CardContent className="py-10 text-center text-sm text-muted-foreground">No staff found.</CardContent></Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((s) => (
              <Card key={s.employee_id} className="bg-card border-border rounded-sm" data-testid={`payroll-emp-${s.employee_id}`}>
                <CardContent className="p-0">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expandedId === s.employee_id ? null : s.employee_id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent/40 transition-colors"
                    data-testid={`payroll-emp-toggle-${s.employee_id}`}
                  >
                    {expandedId === s.employee_id
                      ? <ChevronDown className="w-4 h-4 text-primary flex-shrink-0" />
                      : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{s.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{s.email}</p>
                    </div>
                  </button>
                  {expandedId === s.employee_id && (
                    <div className="border-t border-border/60 px-4 py-3">
                      <StaffPayrollInfo employeeId={s.employee_id} />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PayrollLayout>
  );
}
